import type { KyInstance } from 'ky';

import { ensureError, formatString, isEmpty, isNotDefined } from '@enterprise/core';
import type { OperationResult } from '@enterprise/types';

import type { Tag } from '#tags';

import { Uri } from '#constants/uri';

/**
 * Upserts (inserts or updates) tags for a specific mailing.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param mailingId - The unique identifier of the mailing to update tags for.
 * @param data - An array of Tag objects to be upserted for the mailing.
 * @returns A promise that resolves to an OperationResult indicating the outcome of the operation.
 * @throws {Error} If the mailingId is not defined or the data array is empty.
 * @throws {Error} If the HTTP request fails or another error occurs.
 */
export const upsertMailingTags = async (instance: KyInstance, mailingId: number, data: Tag[]) => {
  try {
    if (isNotDefined(mailingId) || isEmpty(data)) {
      throw new Error('MailingId and tags are required.');
    }

    return await instance
      .post<OperationResult>(formatString(Uri.mailing.upsertTags, mailingId.toString()), {
        json: data,
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
